import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  Lightbulb, Users, Target, Heart, Sparkles, HandHeart,
  ArrowRight, Download, CheckCircle, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";
import { SIX_PILLARS } from "../data/pillars";

const ICONS = { Lightbulb, Users, Target, Heart, Sparkles, HandHeart };

const PILLARS = SIX_PILLARS.map((p) => ({ ...p, icon: ICONS[p.icon] || Sparkles }));

const FrameworkPage = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#F8FAFC] to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">The Foundation of Conscious Business</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1E3A5F] mb-6">
              The Six-Pillar Framework
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              CNESS certification evaluates organisations across six essential dimensions, 
              transforming conscious business from an aspiration into a practical, measurable reality.
            </p>
            <Button 
              className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-3 rounded-xl"
              onClick={() => window.open('/framework-guide.pdf', '_blank')}
            >
              <Download className="w-4 h-4 mr-2" />
              Download the Framework Guide
            </Button>
          </div>
        </div>
      </section>

      {/* Pillars Detail Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {PILLARS.map((pillar, index) => (
              <div 
                key={pillar.id}
                className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center`}
              >
                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: pillar.bgColor }}
                    >
                      <pillar.icon className="w-7 h-7" style={{ color: pillar.color }} />
                    </div>
                    <span 
                      className="text-sm font-semibold px-3 py-1 rounded-full"
                      style={{ backgroundColor: pillar.bgColor, color: pillar.color }}
                    >
                      Pillar {pillar.id}
                    </span>
                  </div>
                  
                  <h2 className="text-2xl sm:text-3xl font-bold text-[#1E3A5F] mb-4">
                    {pillar.name}
                  </h2>
                  
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {pillar.description}
                  </p>
                  
                  <div className="bg-[#F8FAFC] rounded-2xl p-6">
                    <h4 className="font-semibold text-[#1E3A5F] mb-4">Key Practices</h4>
                    <ul className="space-y-3">
                      {pillar.keyPractices.map((practice, i) => (
                        <li key={i} className="flex items-start">
                          <CheckCircle 
                            className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" 
                            style={{ color: pillar.color }} 
                          />
                          <span className="text-gray-600">{practice}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                {/* Visual */}
                <div className="flex-1 w-full max-w-md">
                  <div
                    className="aspect-square rounded-3xl relative overflow-hidden shadow-xl group flex items-center justify-center p-8"
                    style={{ background: `linear-gradient(135deg, ${pillar.bgColor} 0%, white 100%)` }}
                  >
                    <img
                      src={pillar.image}
                      alt={pillar.name}
                      className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105 relative z-10"
                    />

                    {/* Pillar number badge - top left */}
                    <div className="absolute top-6 left-6 z-20 px-4 py-2 rounded-full bg-white/90 backdrop-blur-sm shadow-md">
                      <span
                        className="text-xs font-bold uppercase tracking-widest"
                        style={{ color: pillar.color }}
                      >
                        Pillar {pillar.id}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">
              How the Framework Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Each pillar is evaluated through a comprehensive assessment that examines 
              policies, practices, and measurable outcomes.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Assessment", desc: "Complete a detailed evaluation across all six pillars, identifying your current standing and areas for growth." },
              { step: "02", title: "Scoring", desc: "Receive your Conscious Impact Score (CIS), a comprehensive measure of your organisation's conscious practices." },
              { step: "03", title: "Certification", desc: "Achieve your certification tier based on your performance, with clear pathways for advancement." }
            ].map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                <div className="text-5xl font-bold text-[#FDB813]/30 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1E3A5F]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Assess Your Organisation?
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            Start your certification journey today and discover where your organisation 
            stands across the six pillars of conscious business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/certification')}
              className="w-full sm:w-auto bg-[#FDB813] hover:bg-[#e5a611] text-[#1E3A5F] font-semibold px-8 py-4 rounded-xl"
            >
              Begin Your Assessment
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/contact')}
              className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-[#1E3A5F] px-8 py-4 rounded-xl"
            >
              Schedule a Consultation
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default FrameworkPage;
